import React, { useState, useEffect } from 'react';
import { CanvasWidget, WidgetGroup } from './types';
import { Search, Pause, Maximize2, Minimize2 } from 'lucide-react';
import TokenList from '../TokenList';
import { Token } from '../../types/supabase';

// IMMEDIATE DEBUG LOG TO CHECK IF CANVAS SIDEBAR IS LOADED
console.log('🔥🔥🔥 CanvasSidebar.tsx FILE LOADED');

interface CanvasSidebarProps {
  onAddWidget: (widget: Partial<CanvasWidget>) => void;
  widgetGroups?: WidgetGroup[];
  onShowGroupManager?: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

const CanvasSidebar: React.FC<CanvasSidebarProps> = (props) => {
  // IMMEDIATE DEBUG LOG TO CHECK IF COMPONENT IS RENDERED  
  console.log('🔥🔥🔥 CanvasSidebar COMPONENT RENDERED');
  
  // Destructure for use later if needed
  const { onAddWidget, isExpanded = false, onToggleExpand } = props;
  
  const [activeTab, setActiveTab] = useState<'new' | 'trending' | 'migrated'>('new');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFeedPaused, setIsFeedPaused] = useState(false);
  
  // DEBUG: Log the current state
  console.log('🔥🔥🔥 CanvasSidebar state:', {
    isExpanded,
    activeTab,
    searchTerm,
    isFeedPaused
  });
  
  return (
    <div className={`backdrop-blur-sm bg-dark-grey/40 border-r border-gray-700/30 flex flex-col h-full transition-all duration-300 ${isExpanded ? 'w-[48vw] min-w-[700px]' : 'w-80'}`}>
      {/* Control Bar removed, expand/collapse icon now in Canvas.tsx */}
      {/* Sidebar Tabs (hide in expanded mode) */}
      {!isExpanded && (
        <div className="flex border-b border-gray-700/30 flex-shrink-0 px-0 w-full">
          <button 
            className={`flex-1 py-2 text-xs relative ${activeTab === 'new' ? 'text-[#24c35b]' : 'text-gray-400 hover:text-white'}`}
            onClick={() => {
              console.log('🔥🔥🔥 Clicked NEW tab');
              setActiveTab('new');
            }}
          >
            {activeTab === 'new' && <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-[#24c35b]"></div>}
            {activeTab === 'new' && <div className="absolute inset-0 bg-gradient-to-r from-[#24c35b]/5 to-transparent pointer-events-none"></div>}
            New
          </button>
          <button
            className={`flex-1 py-2 text-xs relative ${activeTab === 'trending' ? 'text-[#24c35b]' : 'text-gray-400 hover:text-[#24c35b]'}`}
            onClick={() => {
              console.log('🔥🔥🔥 Clicked TRENDING tab');
              setActiveTab('trending');
            }}
          >
            {activeTab === 'trending' && <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-[#24c35b]"></div>}
            {activeTab === 'trending' && <div className="absolute inset-0 bg-gradient-to-r from-[#24c35b]/5 to-transparent pointer-events-none"></div>}
            Trending
          </button>
          <button
            className={`flex-1 py-2 text-xs relative ${activeTab === 'migrated' ? 'text-[#24c35b]' : 'text-gray-400 hover:text-[#24c35b]'}`}
            onClick={() => {
              console.log('🔥🔥🔥 Clicked MIGRATED tab');
              setActiveTab('migrated');
            }}
          >
            {activeTab === 'migrated' && <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-[#24c35b]"></div>}
            {activeTab === 'migrated' && <div className="absolute inset-0 bg-gradient-to-r from-[#24c35b]/5 to-transparent pointer-events-none"></div>}
            Migrated
          </button>
        </div>
      )}
      {/* Token Lists */}
      {isExpanded ? (
        <div className="flex flex-row gap-2 px-2 w-full h-full">
          <div className="flex-1 min-w-[260px] h-full flex flex-col">
            <div className="mt-4 mb-2">
              <div className="text-xs font-bold text-[#24c35b] bg-[#18181c] rounded px-3 py-1 shadow border border-[#24c35b]/30 w-fit mx-auto">New</div>
            </div>
            {(() => {
              console.log('🔥🔥🔥 Rendering TokenList for NEW (expanded mode)');
              return <TokenList category="new" searchTerm={searchTerm} onPauseFeed={() => setIsFeedPaused(true)} onResumeFeed={() => setIsFeedPaused(false)} containerClassName="h-full" />;
            })()}
          </div>
          <div className="flex-1 min-w-[260px] h-full flex flex-col">
            <div className="mt-4 mb-2">
              <div className="text-xs font-bold text-[#24c35b] bg-[#18181c] rounded px-3 py-1 shadow border border-[#24c35b]/30 w-fit mx-auto">Trending</div>
            </div>
            {(() => {
              console.log('🔥🔥🔥 Rendering TokenList for TRENDING (expanded mode)');
              return <TokenList category="trending" searchTerm={searchTerm} onPauseFeed={() => setIsFeedPaused(true)} onResumeFeed={() => setIsFeedPaused(false)} containerClassName="h-full" />;
            })()}
          </div>
          <div className="flex-1 min-w-[260px] h-full flex flex-col">
            <div className="mt-4 mb-2">
              <div className="text-xs font-bold text-[#24c35b] bg-[#18181c] rounded px-3 py-1 shadow border border-[#24c35b]/30 w-fit mx-auto">Migrated</div>
            </div>
            {(() => {
              console.log('🔥🔥🔥 Rendering TokenList for MIGRATED (expanded mode)');
              return <TokenList category="migrated" searchTerm={searchTerm} onPauseFeed={() => setIsFeedPaused(true)} onResumeFeed={() => setIsFeedPaused(false)} containerClassName="h-full" />;
            })()}
          </div>
        </div>
      ) : (
        <div 
          className="flex-grow h-0 w-full sidebar-scrollable-container"
          style={{
            overflow: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <style>
            {`
              .sidebar-scrollable-container::-webkit-scrollbar {
                display: none;
              }
              .sidebar-scrollable-container {
                scrollbar-width: none;
                -ms-overflow-style: none;
              }
            `}
          </style>
          {activeTab === 'new' && (() => {
            console.log('🔥🔥🔥 Rendering TokenList for NEW (collapsed mode)');
            return <TokenList category="new" searchTerm={searchTerm} onPauseFeed={() => setIsFeedPaused(true)} onResumeFeed={() => setIsFeedPaused(false)} />;
          })()}
          {activeTab === 'trending' && (() => {
            console.log('🔥🔥🔥 Rendering TokenList for TRENDING (collapsed mode)');
            return <TokenList category="trending" searchTerm={searchTerm} onPauseFeed={() => setIsFeedPaused(true)} onResumeFeed={() => setIsFeedPaused(false)} />;
          })()}
          {activeTab === 'migrated' && (() => {
            console.log('🔥🔥🔥 Rendering TokenList for MIGRATED (collapsed mode)');
            return <TokenList category="migrated" searchTerm={searchTerm} onPauseFeed={() => setIsFeedPaused(true)} onResumeFeed={() => setIsFeedPaused(false)} />;
          })()}
        </div>
      )}
      {/* Glow effects */}
      <div className="relative w-full">
        {/* Green glow at bottom */}
        <div className="absolute -bottom-10 -left-10 w-[100px] h-[100px] rounded-full opacity-30 z-0"
          style={{
            background: 'radial-gradient(circle, rgba(71, 190, 143, 0.2) 0%, rgba(71, 190, 143, 0) 70%)',
            filter: 'blur(20px)'
          }}
        />
      </div>
    </div>
  );
};

export default CanvasSidebar;